import { stopAudioPlayback } from "./lib/audio-player.js";
import { startAudioPlayerWorklet } from "./lib/audio-player.js";
import {
  startAudioRecorderWorklet,
  closeAudioRecorderContext,
  setMicMute,
} from "./lib/audio-recorder.js";
import { toXML } from "jstoxml";

export default defineNuxtPlugin((nuxtApp) => {
  if (import.meta.server) return; // Only run on the client

  // Connect the server with a WebSocket connection
  const sessionId = Math.random().toString().substring(10);

  const localChatbot = useRuntimeConfig().public.localChatbot;
  const ws_url = localChatbot === "enabled" ? "ws://localhost:8000/ws/" + sessionId : "wss://multimodal-v3-521070880790.us-east1.run.app/ws/" + sessionId;


  let websocket = null;
  let is_audio = true;

  let currentMessageId = null;
  let currentContext = null;
  let currentPage = null;
  let currentType = null;

  /**
   *
   *
   * Core Setup
   *
   *
   * */
  function connectWebsocket() {
    console.log("Connecting to websocket", ws_url);
    updateStatusUI("Connecting");

    const user = useCurrentUser();
    const token = user.value.stsTokenManager.accessToken;

    const connection_url = `${ws_url}?is_audio=${is_audio}${currentContext.type ? `&page=${currentContext.type}` : ''}&token=${token}`;
    
    // Connect websocket
    websocket = new WebSocket(connection_url);

    // Handle connection open
    websocket.onopen = function () {
      // Connection opened messages
      console.log("WebSocket connection opened.");
      updateStatusUI("Online");
      switchContext(currentType, currentPage, currentContext);
    };

    // Handle incoming messages
    websocket.onmessage = function (event) {
      // Parse the incoming message
      const message_from_server = JSON.parse(event.data);

      //console.log("[AGENT TO CLIENT] ", message_from_server);
  
      // Check if the turn is complete
      // if turn complete, add new message
      if (
        message_from_server.turn_complete &&
        message_from_server.turn_complete == true
      ) {
        currentMessageId = null;
        return;
      }

      // Check for interrupt message
      if (
        message_from_server.interrupted &&
        message_from_server.interrupted === true
      ) {
        // Stop audio playback if it's playing
        if (audioPlayerNode) {
          audioPlayerNode.port.postMessage({ command: "endOfAudio" });
        }
        return;
      }

      // If it's audio, play it
      if (message_from_server.mime_type == "audio/pcm" && audioPlayerNode) {
        audioPlayerNode.port.postMessage(
          base64ToArray(message_from_server.data)
        );
      }

      // If it's a text, print it
      if (message_from_server.mime_type == "text/plain") {
        console.log("Received text message: ", message_from_server.data);
        // // add a new message for a new turn
        // if (currentMessageId == null) {
        //   currentMessageId = Math.random().toString(36).substring(7);
        //   const message = document.createElement("p");
        //   message.id = currentMessageId;
        //   // Append the message element to the messagesDiv
        //   messagesDiv.appendChild(message);
        // }

        // Add message text to the existing message element
        // const message = document.getElementById(currentMessageId);
        // message.textContent += message_from_server.data;

        // Scroll down to the bottom of the messagesDiv
        // messagesDiv.scrollTop = messagesDiv.scrollHeight;
      }

      // If it's a transcript, add it to the transcript
      if(message_from_server.transcript && message_from_server.transcript.length > 0) {
        appendTranscriptUI(message_from_server.transcript, message_from_server.role);
      }
    };

    // Handle connection close
    websocket.onclose = function () {
      console.log("WebSocket connection closed.");
      stopAudioRecording();
      stopAudioPlayback();
    };

    websocket.onerror = function (e) {
      console.error("WebSocket error: ", e);
      updateStatusUI("Error");
      // Cleanup
      stopAudioRecording();
      stopAudioPlayback();
    };
  }

  // Send a message to the server as a JSON string
  function sendMessage(message) {
    if (websocket && websocket.readyState == WebSocket.OPEN) {
      const messageJson = JSON.stringify(message);
      websocket.send(messageJson);
    }
  }

  // Decode Base64 data to Array
  function base64ToArray(base64) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Audio handling
   */

  let audioPlayerNode;
  let audioPlayerContext;
  let audioRecorderNode;
  let audioRecorderContext;
  let micStream;

  // Audio buffering for 0.2s intervals
  let audioBuffer = [];
  let bufferTimer = null;

  // Start audio
  async function startAudio() {
    // Start audio output
    startAudioPlayerWorklet().then(([node, ctx]) => {
      audioPlayerNode = node;
      audioPlayerContext = ctx;
    });
    // Start audio input
    startAudioRecorderWorklet(audioRecorderHandler).then(
      ([node, ctx, stream]) => {
        audioRecorderNode = node;
        audioRecorderContext = ctx;
        micStream = stream;
      }
    );
  }

  // Audio recorder handler
  function audioRecorderHandler(pcmData) {
    // Add audio data to buffer
    audioBuffer.push(new Uint8Array(pcmData));

    // Start timer if not already running
    if (!bufferTimer) {
      bufferTimer = setInterval(sendBufferedAudio, 200); // 0.2 seconds
    }
  }

  // Send buffered audio data every 0.2 seconds
  function sendBufferedAudio() {
    if (audioBuffer.length === 0) {
      return;
    }

    // Calculate total length
    let totalLength = 0;
    for (const chunk of audioBuffer) {
      totalLength += chunk.length;
    }

    // Combine all chunks into a single buffer
    const combinedBuffer = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of audioBuffer) {
      combinedBuffer.set(chunk, offset);
      offset += chunk.length;
    }

    // Send the combined audio data
    sendMessage({
      mime_type: "audio/pcm",
      data: arrayBufferToBase64(combinedBuffer.buffer),
    });
    console.log("[CLIENT TO AGENT] sent %s bytes", combinedBuffer.byteLength);

    // Clear the buffer
    audioBuffer = [];
  }

  // Stop audio recording and cleanup
  function stopAudioRecording() {
    console.log("Stopping audio recording", bufferTimer, audioBuffer.length);
    if (bufferTimer) {
      clearInterval(bufferTimer);
      bufferTimer = null;
    }

    // Send any remaining buffered audio
    if (audioBuffer.length > 0) {
      sendBufferedAudio();
    }

    closeAudioRecorderContext();
  }

  // Encode an array buffer with Base64
  function arrayBufferToBase64(buffer) {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   *
   *
   * Additional Functionality
   *
   *
   */

  // Context management
  const switchContext = (type, page, data) => {
    if (websocket && websocket.readyState == WebSocket.OPEN) {
      const messageJson = JSON.stringify({
        mime_type: "text/xml",
        data: toXML({PAGE_CONTENT: data}),
      });
      websocket.send(messageJson);
    }
  };

  // Setup UI Bindings
  let connectUI = null;
  let updateStatusUI = null;
  let selectPhotoByIndexUI = null;
  let appendTranscriptUI = null;
  
  const setupConnectUI = (func) => {
    connectUI = func;
  };

  const setupUpdateStatusUI = (func) => {
    updateStatusUI = func;
  };

  const setupSelectPhotoByIndexUI = (func) => {
    selectPhotoByIndexUI = func;
  };

  const setupAppendTranscriptUI = (func) => {
    appendTranscriptUI = func;
  };
  const micMute = (mute) => {
    setMicMute(mute);
  };

  return {
    provide: {
      connect: () => {
        startAudio();
        connectWebsocket();
      },
      disconnect: () => {
        updateStatusUI("HangUp");
        websocket.close();
      },
      informAssistant: (type, page, data) => {
        console.log("Informing assistant of context change", {
          type,
          page,
          data,
        });
        currentType = type;
        currentPage = page;
        currentContext = data;
        switchContext(type, page, data);
      },

      // Stores context for the user's current page
      setContext: (type, page, data) => {
        currentContext = {
          type,
          page,
          data,
        };
        switchContext(type, page, data);
      },

      // UI Bindings
      setupConnectUI: setupConnectUI,
      connectThroughUI: () => {
        if (connectUI) {
          connectUI();
        }
      },
      setupUpdateStatusUI: setupUpdateStatusUI,
      updateStatus: (status) => {
        if (updateStatusUI) {
          updateStatusUI(status);
        }
      },
      setupSelectPhotoByIndexUI: setupSelectPhotoByIndexUI,
      selectPhotoByIndex: (index) => {
        if (selectPhotoByIndexUI) {
          selectPhotoByIndexUI(index);
        }
      },
      setupAppendTranscriptUI: setupAppendTranscriptUI,
      appendTranscript: (transcript) => {
        if (appendTranscriptUI) {
          appendTranscriptUI(transcript);
        }
      },
      micMute: micMute,
    },
  };
});
